import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtDate, hasAiConsent, needsAiConsentDecision, type AiConsent } from '@immoaktas/shared';
import { api, useAuth } from '../../../lib/api';
import { useNotify } from '../../../lib/hooks';
import { Button, Card, Dialog, Pill } from '../../../ui';
import { siteUrl } from '../../../lib/purchase';

/** Keeps the session's `me` in sync after a consent change (the server is the source of truth). */
function storeAiConsent(aiConsent: AiConsent) {
  const st = useAuth.getState();
  if (st.me) st.set({ me: { ...st.me, user: { ...st.me.user, aiConsent } } });
}

export type AiDecision = 'granted' | 'declined' | 'cancelled';

/**
 * Consent gate before the first upload that would use the receipt AI (Apple 5.1.2(i), GDPR).
 * `ensure()` resolves at once when the person already decided; otherwise it shows the dialog.
 * "Declined" still uploads – the server keeps the receipt for manual entry. Closing = cancel the upload.
 */
export function useAiConsentGate(): { ensure: (opts?: { force?: boolean }) => Promise<AiDecision>; dialog: ReactNode } {
  const { t } = useTranslation();
  const toast = useNotify();
  const consent = useAuth((s) => s.me?.user.aiConsent);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = useRef<((d: AiDecision) => void) | null>(null);

  const finish = useCallback((d: AiDecision) => {
    setOpen(false);
    pending.current?.(d);
    pending.current = null;
  }, []);
  /** force: ask even after a "no" (e.g. "read with AI now" on a manual receipt). */
  const ensure = useCallback((opts: { force?: boolean } = {}) => {
    if (hasAiConsent(consent)) return Promise.resolve<AiDecision>('granted');
    if (!opts.force && !needsAiConsentDecision(consent)) return Promise.resolve<AiDecision>('declined');
    return new Promise<AiDecision>((resolve) => {
      pending.current = resolve;
      setOpen(true);
    });
  }, [consent]);
  const decide = async (granted: boolean) => {
    setBusy(true);
    try {
      const r = granted ? await api.me.grantAiConsent() : await api.me.revokeAiConsent();
      storeAiConsent(r.aiConsent);
      finish(granted ? 'granted' : 'declined');
    } catch (e) {
      // A failed "no" still means no AI: the server only uses AI with a stored "yes".
      if (granted) toast.err(e);
      else finish('declined');
    } finally {
      setBusy(false);
    }
  };

  const dialog = (
    <Dialog open={open} onClose={() => finish('cancelled')} title={t('common:aiConsent.title')}
      actions={<><Button variant="secondary" disabled={busy} onClick={() => void decide(false)}>{t('common:aiConsent.decline')}</Button><Button loading={busy} onClick={() => void decide(true)}>{t('common:aiConsent.accept')}</Button></>}>
      <AiConsentText />
    </Dialog>
  );
  return { ensure, dialog };
}

/** The consent text itself (what, to whom, what for, no training, can decline, can change). */
export function AiConsentText() {
  const { t } = useTranslation();
  return (
    <div className="stack tight">
      <p>{t('common:aiConsent.intro')}</p>
      <ul className="small" style={{ paddingLeft: 18, margin: 0 }}>
        <li>{t('common:aiConsent.what')}</li>
        <li>{t('common:aiConsent.who')}</li>
        <li>{t('common:aiConsent.why')}</li>
        <li>{t('common:aiConsent.training')}</li>
      </ul>
      <p className="small">{t('common:aiConsent.choice')}</p>
      <p className="small"><a href={siteUrl("https://immoaktas.com/datenschutz")} target="_blank" rel="noreferrer">{t('common:aiConsent.privacy')}</a></p>
    </div>
  );
}

/** Settings: show and change the person's own AI consent at any time. */
export function AiConsentCard() {
  const { t } = useTranslation();
  const toast = useNotify();
  const consent = useAuth((s) => s.me?.user.aiConsent);
  const [busy, setBusy] = useState(false);
  const on = hasAiConsent(consent);
  const toggle = async () => {
    setBusy(true);
    try {
      const r = on ? await api.me.revokeAiConsent() : await api.me.grantAiConsent();
      storeAiConsent(r.aiConsent);
      toast.ok(on ? t('common:aiConsent.revoked') : t('common:aiConsent.granted'));
    } catch (e) {
      toast.err(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card level={2} roomy title={t('common:aiConsent.settingsTitle')} actions={<Pill tone={on ? 'ok' : 'neutral'} size="md">{on ? t('common:aiConsent.on') : t('common:aiConsent.off')}</Pill>}>
      <div className="stack">
        <p className="small">{on && consent ? t('common:aiConsent.settingsOn', { date: fmtDate(consent.at) }) : t('common:aiConsent.settingsOff')}</p>
        {!on && <details><summary className="small">{t('common:aiConsent.title')}</summary><div style={{ marginTop: 8 }}><AiConsentText /></div></details>}
        <p className="small muted">{t('common:aiConsent.settingsPersonal')}</p>
        <div><Button variant={on ? 'secondary' : 'primary'} loading={busy} onClick={() => void toggle()}>{on ? t('common:aiConsent.revoke') : t('common:aiConsent.grant')}</Button></div>
      </div>
    </Card>
  );
}
